import CustomPagination from "components/CustomPagination";
import Loading from "components/Loading";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchSuspectToWitnessByCRNumberAndIncidentRegNo } from "services/caseServices";
import { SuspectTowitness, SuspectToWitnessStatus } from "types";
import { errorHandler } from "utils/helper";

type Props = {
  crimeReferenceNumber?: string;
  incidentRegistrationNumber?: string;
  setSelectedId: React.Dispatch<React.SetStateAction<string | number>>;
  setPageType: React.Dispatch<React.SetStateAction<"list" | "detail">>;
};

const ITEMS_PER_PAGE = 9;

const SuspectToWitnessList: React.FC<Props> = ({
  crimeReferenceNumber,
  incidentRegistrationNumber,
  setSelectedId,
  setPageType,
}) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  // const { loginData } = useAuth();

  const [witnessList, setWitnessList] = useState<SuspectTowitness[]>([]);
  const [loading, setLoading] = useState(true);

  // const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  // const isProsecutor = hasRole(loginData?.roles, [Roles.prosecutor]);

  const fetchData = () => {
    setLoading(true);
    fetchSuspectToWitnessByCRNumberAndIncidentRegNo(
      crimeReferenceNumber || "",
      incidentRegistrationNumber || "",
    )
      .then((data) => {
        setWitnessList(data);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(errorHandler(error));
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  // const searchTerm = search.trim().toLowerCase();
  // const filtered = witnessList.filter((item) => {
  //   const prosecutor_name =
  //     item?.prosecutor_name?.toString()?.toLowerCase() || "";
  //   const decision = item?.decision?.toString()?.toLowerCase() || "";
  //   const status = item?.status?.toString()?.toLowerCase() || "";
  //   const first_name =
  //     item?.suspect_info?.first_name?.toString()?.toLowerCase() || "";
  //   const middle_name =
  //     item?.suspect_info?.middle_name?.toString()?.toLowerCase() || "";
  //   const last_name =
  //     item?.suspect_info?.last_name?.toString()?.toLowerCase() || "";

  //   const matchesText =
  //     prosecutor_name.includes(searchTerm) ||
  //     decision.includes(searchTerm) ||
  //     status.includes(searchTerm) ||
  //     first_name.includes(searchTerm) ||
  //     middle_name.includes(searchTerm) ||
  //     last_name.includes(searchTerm);

  //   return matchesText;
  // });

  // Pagination logic
  const pageCount = Math.ceil(witnessList.length / ITEMS_PER_PAGE);
  const paginated = witnessList.slice(
    currentPage * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="max-w-screen-xl ml-1 space-y-6">
      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {/* <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 border-b">
            <div className="relative w-full md:w-80">
              <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                🔍
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="Search"
                className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
              />
            </div>
          </div> */}
          {loading ? (
            <Loading label={t("loading_suspect_to_witness")} />
          ) : paginated.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {t("no_suspect_to_witness_found")}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
              {paginated.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col gap-2 transition-all duration-150 hover:shadow-lg"
                >
                  <div className="flex flex-row justify-between items-center mb-2">
                    <span className="font-semibold text-[#AA232F] text-base">
                      {item?.suspect_info
                        ? `${item.suspect_info.first_name || ""} ${
                            item.suspect_info.middle_name || ""
                          } ${item.suspect_info.last_name || ""}`.trim()
                        : item.suspect}
                    </span>
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        item?.status === "Pending"
                          ? "bg-yellow-100 text-yellow-700"
                          : item?.status === "Respond_Back_prosecutor"
                            ? "bg-blue-100 text-blue-700"
                            : item?.status === "forwarded_to_cordinator"
                              ? "bg-purple-100 text-purple-700"
                              : item?.status === "forwarded_to_attorney_general"
                                ? "bg-indigo-100 text-indigo-700"
                                : item?.status ===
                                    "forwarded_to_head_of_attorney_general"
                                  ? "bg-pink-100 text-pink-700"
                                  : item?.status === "forwarded_to_registrar"
                                    ? "bg-orange-100 text-orange-700"
                                    : item?.status === "sent_to_court"
                                      ? "bg-green-100 text-green-700"
                                      : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {item.status === "forwarded_to_cordinator" && item?.org_id
                        ? t("forward_to_director")
                        : item?.status === "forwarded_to_attorney_general"
                          ? t("forward_to_deputy")
                          : t(`status_${(item?.status || "").toLowerCase()}`, {
                              defaultValue:
                                SuspectToWitnessStatus[item.status] ||
                                item.status,
                            })}
                      {/* {item?.status && SuspectToWitnessStatus[item.status]} */}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 mb-2">
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">
                        {t("prosecutor_name")}:
                      </span>
                      {item?.prosecutor_name || ""}
                    </span>
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">{t("decision")}: </span>
                      {item?.decision}
                    </span>
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">{t("suspect")}: </span>
                      {item?.suspect_info?.first_name}{" "}
                      {item?.suspect_info?.middle_name}{" "}
                      {item?.suspect_info?.last_name}
                    </span>
                  </div>

                  <div className="flex flex-row justify-end gap-2 mt-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(item?.id ?? "");
                        setPageType("detail");
                      }}
                      className="px-4 py-1 text-sm bg-[#212121] text-white rounded hover:bg-[#AA232F] transition"
                    >
                      {t("view")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex justify-center mb-8">
              <CustomPagination
                pageCount={pageCount}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SuspectToWitnessList;
